<template>
  <v-card>
    <slot name="default"></slot>
    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <v-card>
          <v-card-text :style="cardStyle" v-html="content"></v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn style="margin:0 auto" color="info" @click="begin()">{{ isReturning ? 'Continue' : 'Begin' }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-tab-item>
      <v-tab-item v-for="(input, index) in questions" :key="'q-' + index" :value="index + 1">
        <v-card>
          <v-card-text :style="cardStyle">
            <span v-if="input.text" v-html="input.text"></span>
            <component
              :is="input.type"
              :label="input.label"
              :items="input.answers"
              :disabled="input.chosenAnswer != null && !correctToProceed"
              :value="input.chosenAnswer"
              outlined
              @change="(val) => answered(index, val)">
            </component>
            <v-alert
              v-if="input.chosenAnswer != null"
              :type="input.chosenAnswer === input.correctAnswer ? 'success' : 'error'"
              :icon="input.chosenAnswer === input.correctAnswer ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'">
              <span v-html="input.responseText"></span>
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="back()">Back</v-btn>
            <v-spacer></v-spacer>
            Question {{ index + 1 }}/{{ questions.length }}
            <v-spacer></v-spacer>
            <v-btn :disabled="!input.isCorrect && correctToProceed" color="info" @click="next()">{{ index === questions.length - 1 ? 'Finish' : 'Next' }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-tab-item>
      <v-tab-item :value="questions.length + 1">
        <v-card>
          <v-card-text :style="cardStyle" v-html="parsedFinalText"></v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>
<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from '@vue/composition-api'
export default {
  props: {
    section: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    }
  },
  components: {
    Radio: () => import('@/components/forms/inputs/Radio'),
    Select: () => import('vuetify/lib/components/VSelect'),
    Autocomplete: () => import('vuetify/lib/components/VAutocomplete')
  },
  setup (props, { root, emit }) {
    const title = ref('')
    const content = ref('')
    const correctToProceed = ref(false)
    const oversized = ref(false)
    const activeTab = ref(0)
    const questions = ref([])
    const finalText = ref('')
    const trackerId = computed(() => root.$store.state.tracker.id)
    const trackerQuizIndex = ref(null)

    const cardStyle = computed(() => {
      return 'height:' + (oversized.value ? '600px' : '400px')
    })

    const trackServ = root.$feathers.service('orientation/tracker')
    const blockServ = root.$feathers.service('orientation/progress/block')
    const progressBlockId = ref(null)

    const onPatchedListener = (data) => { if (data._id === props.id) loadBlock() }
    onMounted(() => { root.$feathers.service('orientation/block').on('patched', onPatchedListener) })
    onBeforeUnmount(() => { root.$feathers.service('orientation/block').removeListener('patched', onPatchedListener) })

    function loadBlock () {
      root.$feathers.service('orientation/block').get(props.id).then((data) => {
        title.value = data.title
        content.value = data.content.replace(/<a /ig, '<a target="_blank"')
        correctToProceed.value = data.correctToProceed || false
        oversized.value = 'oversized' in data ? data.oversized : false
        finalText.value = data.finalText.replace(/<a /ig, '<a target="_blank"')
        questions.value = []
        for (let i = 0; i < data.questions.length; i++) {
          questions.value.push({ ...data.questions[i], chosenAnswer: null, isCorrect: false, responseText: '' })
        }
        emit('activeWaiting')
      })
    }
    loadBlock()

    function back () {
      activeTab.value--
    }
    function next () {
      activeTab.value++
    }

    function answered (questionIndex, answerIndex) {
      if (answerIndex == null || answerIndex === '') return
      let temp = JSON.parse(JSON.stringify(questions.value[questionIndex]))
      if (typeof (answerIndex) === 'string') {
        // We have the string of the answer instead of the index; go through the list of answers and find the matching one
        for (let i = 0; i < temp.answers.length; i++) {
          if (temp.answers[i].text === answerIndex) {
            answerIndex = i
            break
          }
        }
      }
      // Check if the given answer is the correct one or an incorrect one
      temp.chosenAnswer = answerIndex
      if (temp.chosenAnswer === temp.correctAnswer) {
        temp.isCorrect = true
      } else {
        temp.isCorrect = false
      }
      if (answerIndex != null && answerIndex in temp.answers) temp.responseText = temp.answers[answerIndex].response || (temp.isCorrect ? 'You have chosen the correct answer.' : 'You have chosen an incorrect answer.')
      questions.value.splice(questionIndex, 1, temp)

      // Count the number of answers they have answered correctly
      let correct = 0
      questions.value.forEach(({ isCorrect }) => {
        if (isCorrect) correct++
      })
      // Build the patch object to update the tracker
      let patch = {
        ['blocks.' + trackerQuizIndex.value + '.quiz.answers.' + questionIndex]: answerIndex,
        ['blocks.' + trackerQuizIndex.value + '.quiz.correct']: correct
      }
      // Update the tracker with the new answer and the number of correct answers
      trackServ.patch(trackerId.value, patch).then(({ blocks }) => {
        // Check to see if this is the first time they have answered this question; if so, patch the block with the first answer
        if (!(questionIndex in blocks[trackerQuizIndex.value].quiz.firstAnswer) || blocks[trackerQuizIndex.value].quiz.firstAnswer[questionIndex] == null) {
          trackServ.patch(trackerId.value, { ['blocks.' + trackerQuizIndex.value + '.quiz.firstAnswer.' + questionIndex]: answerIndex })
        }
        root.$store.commit('setTrackerBlocks', blocks)
      })
      blockServ.patch(progressBlockId.value, { ['quiz.answers.' + questionIndex]: answerIndex, 'quiz.correct': correct }).then((data) => {
        if (!(questionIndex in data.quiz.firstAnswer)) {
          blockServ.patch(progressBlockId.value, { ['quiz.firstAnswer.' + questionIndex]: answerIndex })
        }
      })
    }

    const parsedFinalText = computed(() => {
      let text = finalText.value
      let correctAnswers = 0
      for (let i = 0; i < questions.value.length; i++) {
        let question = questions.value[i]
        let answerText = ''
        if (question.chosenAnswer != null && question.chosenAnswer in question.answers) {
          answerText = question.answers[question.chosenAnswer].text
          if (question.chosenAnswer === question.correctAnswer) correctAnswers++
        }
        text = text.replace('<code>' + question.label + '</code>', answerText)
      }
      text = text.replace('<code>Quiz Score</code>', correctAnswers + '/' + questions.value.length)
      text = text.replace('<code>Quiz Grade</code>', parseInt(correctAnswers / questions.value.length * 100) + '%')
      return text
    })

    onMounted(() => {
      if (root.$route.path.split('/')[1] !== 'admin') {
        root.$store.dispatch('getTracker').then(({ blocks }) => {
          for (let i = 0; i < blocks.length; i++) {
            if (blocks[i].section === props.section && JSON.stringify(blocks[i].block) === JSON.stringify(props.id)) {
              trackerQuizIndex.value = i
              let { answers } = blocks[i].quiz
              // If they have already completed this quiz allow them to move on without doing it again
              if (answers != null) {
                for (let j = 0; j < questions.value.length; j++) {
                  if (j in answers) {
                    questions.value[j].chosenAnswer = answers[j]
                    if (questions.value[j].correctAnswer === answers[j]) lastQuestion.value = j
                  }
                }
              }
              if (blocks[i].complete) {
                activeTab.value = questions.value.length + 1
                emit('activeFinish')
              } else {
                isReturning.value = true
              }
              break
            }
          }
          if (trackerQuizIndex.value === null) {
            trackServ.patch(trackerId.value, { $push: { blocks: {
              section: props.section,
              block: props.id,
              complete: false,
              quiz: {
                questions: questions.value.length,
                correct: 0,
                answers: [],
                firstAnswer: []
              }
            } } }).then(({ blocks }) => {
              trackerQuizIndex.value = blocks.length - 1
              root.$store.commit('setTrackerBlocks', blocks)
            })
          }
          blockServ.find({ query: { pidm: root.$store.state.user.pidm, block: props.id } }).then(({ data }) => {
            if (data.length === 0) {
              blockServ.create({ pidm: root.$store.state.user.pidm, term: root.$store.state.term, section: props.section, block: props.id }).then((data) => {
                progressBlockId.value = data._id
              })
            } else {
              progressBlockId.value = data._id
            }
          })
        })
      }
    })

    watch(activeTab, () => {
      // Check to see if we are on the final page of the quiz; if so then that means they have finished
      if (activeTab.value > questions.value.length) {
        if (root.$route.path.split('/')[1] !== 'admin') {
          let numCorrect = 0
          let answers = []
          for (let i = 0; i < questions.value.length; i++) {
            let question = questions.value[i]
            if (question.chosenAnswer != null && question.chosenAnswer in question.answers) {
              if (question.chosenAnswer === question.correctAnswer) numCorrect++
              answers.push(question.chosenAnswer)
            }
          }
          trackServ.patch(trackerId.value, {
            ['blocks.' + trackerQuizIndex.value + '.complete']: true,
            ['blocks.' + trackerQuizIndex.value + '.quiz.correct']: numCorrect
          })
          blockServ.patch(progressBlockId.value, {
            complete: true,
            'quiz.correct': numCorrect
          })
        }
        emit('activeFinish')
      }
    })
    const isReturning = ref(false)
    const lastQuestion = ref(null)
    function begin () {
      if (isReturning.value === true && lastQuestion.value != null) {
        activeTab.value = lastQuestion.value + 2
      } else {
        activeTab.value++
      }
    }

    return {
      title,
      content,
      correctToProceed,
      finalText,
      trackerId,
      trackerQuizIndex,
      cardStyle,
      activeTab,
      questions,
      back,
      next,
      answered,
      parsedFinalText,
      isReturning,
      begin
    }
  }
}
</script>
